import React from 'react';

const Superannuation: React.FC = () => {
  return (
    <>
      <style>{`
        .sp *, .sp *::before, .sp *::after { box-sizing: border-box; margin: 0; padding: 0; }
        .sp {
          font-family: 'Inter', sans-serif;
          background: #ffffff;
          color: #1a1a2e;
          line-height: 1.6;
        }

        /* ─── HERO ──────────────────────────────────────────── */
        .sp-hero {
          background: linear-gradient(150deg, #0a1628 0%, #0d1b3e 55%, #0f2652 100%);
          padding: 88px 48px 96px;
          text-align: center;
          position: relative;
          overflow: hidden;
        }
        .sp-hero::before {
          content: '';
          position: absolute;
          inset: 0;
          background:
            radial-gradient(ellipse at 80% 30%, rgba(0,196,180,0.09) 0%, transparent 50%),
            radial-gradient(ellipse at 15% 80%, rgba(59,130,246,0.06) 0%, transparent 45%);
        }
        .sp-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(0,196,180,0.12);
          border: 1px solid rgba(0,196,180,0.28);
          color: #00c4b4;
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          padding: 8px 20px;
          border-radius: 100px;
          margin-bottom: 36px;
          position: relative;
        }
        .sp-hero h1 {
          font-family: 'Manrope', sans-serif;
          font-size: clamp(2.2rem, 5.5vw, 4rem);
          font-weight: 800;
          color: #ffffff;
          line-height: 1.1;
          margin-bottom: 24px;
          position: relative;
        }
        .sp-hero h1 em { font-style: normal; color: #00c4b4; }
        .sp-hero-sub {
          font-size: clamp(1rem, 2vw, 1.2rem);
          color: rgba(255,255,255,0.75);
          max-width: 680px;
          margin: 0 auto 40px;
          position: relative;
          line-height: 1.75;
        }
        .sp-hero-pause {
          font-family: 'Manrope', sans-serif;
          font-size: clamp(1rem, 2.2vw, 1.3rem);
          color: rgba(255,255,255,0.5);
          font-style: italic;
          position: relative;
          margin-bottom: 44px;
        }
        .sp-hero-trust {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 28px;
          flex-wrap: wrap;
          position: relative;
        }
        .sp-hero-trust-item {
          display: flex; align-items: center; gap: 7px;
          font-size: 0.82rem;
          color: rgba(255,255,255,0.45);
        }
        .sp-hero-trust-item svg { color: #00c4b4; flex-shrink: 0; }

        /* ─── DISCLAIMER ─────────────────────────────────────── */
        .sp-disclaimer {
          background: #f4f6fb;
          border-top: 3px solid rgba(0,196,180,0.5);
          padding: 18px 32px;
          text-align: center;
        }
        .sp-disclaimer p {
          font-size: 0.82rem;
          color: #4b5563;
          line-height: 1.6;
          max-width: 860px;
          margin: 0 auto;
        }
        .sp-disclaimer strong { font-weight: 700; }

        /* ─── SECTION WRAPPERS ───────────────────────────────── */
        .sp-section { padding: 88px 24px; }
        .sp-section-alt { padding: 88px 24px; background: #f4f6fb; }
        .sp-section-dark {
          padding: 88px 24px;
          background: linear-gradient(135deg, #0a1628 0%, #0d1b3e 60%, #0f2652 100%);
          position: relative;
          overflow: hidden;
        }
        .sp-section-dark::before {
          content: '';
          position: absolute;
          inset: 0;
          background: radial-gradient(ellipse at 70% 50%, rgba(0,196,180,0.08) 0%, transparent 55%);
        }

        /* ─── SHARED HEADER ──────────────────────────────────── */
        .sp-header {
          text-align: center;
          max-width: 720px;
          margin: 0 auto 56px;
        }
        .sp-header-dark { color: #ffffff; }
        .sp-badge {
          display: inline-block;
          font-size: 0.68rem;
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          padding: 7px 18px;
          border-radius: 100px;
          margin-bottom: 20px;
        }
        .sp-badge-teal { background: rgba(0,196,180,0.15); border: 1px solid rgba(0,196,180,0.3); color: #00c4b4; }
        .sp-badge-red { background: #f4f6fb; border: 1px solid #e5e9f2; color: #1a1a2e; }
        .sp-badge-amber { background: #f4f6fb; border: 1px solid #e5e9f2; color: #6b7280; }
        .sp-badge-green { background: rgba(0,196,180,0.12); border: 1px solid rgba(0,196,180,0.28); color: #00c4b4; }
        .sp-badge-blue { background: rgba(13,27,62,0.06); border: 1px solid rgba(13,27,62,0.12); color: #0d1b3e; }
        .sp-header h2 {
          font-family: 'Manrope', sans-serif;
          font-size: clamp(1.9rem, 4vw, 2.9rem);
          font-weight: 800;
          color: #1a1a2e;
          line-height: 1.15;
          margin-bottom: 18px;
        }
        .sp-header-dark h2 { color: #ffffff; }
        .sp-header p {
          font-size: 1.05rem;
          color: #6b7280;
          line-height: 1.75;
        }
        .sp-header-dark p { color: rgba(255,255,255,0.6); }

        /* ─── BIG QUESTION ───────────────────────────────────── */
        .sp-big-q {
          max-width: 860px;
          margin: 0 auto;
          text-align: center;
          padding: 0 24px;
        }
        .sp-big-q-text {
          font-family: 'Manrope', sans-serif;
          font-size: clamp(2.4rem, 6vw, 4.8rem);
          font-weight: 800;
          color: #ffffff;
          line-height: 1.12;
          margin-bottom: 32px;
          position: relative;
        }
        .sp-big-q-sub {
          font-size: clamp(1rem, 2vw, 1.2rem);
          color: rgba(255,255,255,0.58);
          line-height: 1.75;
          max-width: 600px;
          margin: 0 auto;
          position: relative;
        }

        /* ─── GAP SECTION ────────────────────────────────────── */
        .sp-gap-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
          max-width: 860px;
          margin: 0 auto 48px;
        }
        .sp-gap-card {
          border-radius: 20px;
          padding: 40px 32px;
          text-align: center;
        }
        .sp-gap-card-have {
          background: #f9fafc;
          border: 1px solid #e5e9f2;
        }
        .sp-gap-card-need {
          background: #0d1b3e;
          border: 1px solid #1e3060;
        }
        .sp-gap-label {
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          margin-bottom: 16px;
        }
        .sp-gap-label-muted { color: #9ca3af; }
        .sp-gap-label-light { color: rgba(255,255,255,0.45); }
        .sp-gap-stat {
          font-family: 'Manrope', sans-serif;
          font-size: clamp(2.6rem, 6vw, 4rem);
          font-weight: 800;
          line-height: 1;
          margin-bottom: 12px;
        }
        .sp-gap-stat-dark { color: #1a1a2e; }
        .sp-gap-stat-teal { color: #00c4b4; }
        .sp-gap-desc { font-size: 0.9rem; line-height: 1.6; }
        .sp-gap-desc-muted { color: #6b7280; }
        .sp-gap-desc-light { color: rgba(255,255,255,0.55); }
        .sp-gap-verdict {
          max-width: 860px;
          margin: 0 auto;
          background: #0d1b3e;
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 14px;
          padding: 24px 32px;
          text-align: center;
        }
        .sp-gap-verdict p {
          font-family: 'Manrope', sans-serif;
          font-size: clamp(1rem, 2.2vw, 1.25rem);
          font-weight: 700;
          color: #ffffff;
          line-height: 1.5;
        }

        /* ─── CONTROL SECTION ────────────────────────────────── */
        .sp-control-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          max-width: 800px;
          margin: 0 auto 40px;
        }
        .sp-control-card {
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 14px;
          padding: 24px 20px;
          display: flex;
          align-items: center;
          gap: 14px;
          position: relative;
        }
        .sp-control-icon {
          width: 40px; height: 40px;
          background: rgba(0,196,180,0.1);
          border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
          color: #00c4b4;
        }
        .sp-control-label { font-size: 0.95rem; font-weight: 600; color: rgba(255,255,255,0.85); }
        .sp-control-sub { font-size: 0.8rem; color: rgba(255,255,255,0.4); margin-top: 2px; }
        .sp-control-verdict {
          max-width: 800px;
          margin: 0 auto;
          text-align: center;
          position: relative;
        }
        .sp-control-verdict-text {
          font-family: 'Manrope', sans-serif;
          font-size: clamp(1.6rem, 3.5vw, 2.4rem);
          font-weight: 800;
          color: #ffffff;
          line-height: 1.2;
          margin-bottom: 14px;
        }
        .sp-control-verdict-sub {
          font-size: 1rem;
          color: rgba(255,255,255,0.5);
          font-style: italic;
        }

        /* ─── SMSF COMPARE ───────────────────────────────────── */
        .sp-compare {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          max-width: 800px;
          margin: 0 auto 40px;
        }
        .sp-compare-card {
          border-radius: 18px;
          padding: 36px 28px;
          text-align: center;
        }
        .sp-compare-old {
          background: #f4f6fb;
          border: 1px solid #e5e9f2;
        }
        .sp-compare-new {
          background: #0d1b3e;
          border: 1px solid rgba(0,196,180,0.2);
        }
        .sp-compare-label {
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          margin-bottom: 14px;
        }
        .sp-compare-label-muted { color: #9ca3af; }
        .sp-compare-label-teal { color: #00c4b4; }
        .sp-compare-title {
          font-family: 'Manrope', sans-serif;
          font-size: 1.35rem;
          font-weight: 800;
          margin-bottom: 10px;
        }
        .sp-compare-title-dark { color: #1a1a2e; }
        .sp-compare-title-white { color: #ffffff; }
        .sp-compare-desc { font-size: 0.9rem; line-height: 1.65; }
        .sp-compare-desc-muted { color: #6b7280; }
        .sp-compare-desc-light { color: rgba(255,255,255,0.55); }
        .sp-compare-note {
          font-size: 0.78rem;
          font-weight: 600;
          margin-top: 16px;
          padding: 8px 16px;
          border-radius: 8px;
          display: inline-block;
        }
        .sp-compare-note-grey { background: #e5e9f2; color: #6b7280; }
        .sp-compare-note-teal { background: rgba(0,196,180,0.15); color: #00c4b4; }

        /* ─── TAX TABLE ──────────────────────────────────────── */
        .sp-tax-table {
          max-width: 640px;
          margin: 0 auto;
          border-radius: 18px;
          overflow: hidden;
          box-shadow: 0 4px 32px rgba(0,0,0,0.08);
        }
        .sp-tax-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          align-items: center;
        }
        .sp-tax-row-head {
          background: #0d1b3e;
          padding: 16px 28px;
        }
        .sp-tax-row-head span {
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.5);
        }
        .sp-tax-cell {
          padding: 22px 28px;
          border-bottom: 1px solid #f0f2f8;
        }
        .sp-tax-cell:nth-child(even) {
          text-align: right;
          font-family: 'Manrope', sans-serif;
          font-size: 1.6rem;
          font-weight: 800;
        }
        .sp-tax-cell-bg-1 { background: #ffffff; }
        .sp-tax-cell-bg-2 { background: #f9fafc; }
        .sp-tax-cell-bg-3 { background: rgba(0,196,180,0.06); }
        .sp-tax-label { font-size: 0.95rem; font-weight: 600; color: #1a1a2e; }
        .sp-tax-sub { font-size: 0.78rem; color: #9ca3af; margin-top: 3px; }
        .sp-tax-rate-red { color: #6b7280; }
        .sp-tax-rate-amber { color: #6b7280; }
        .sp-tax-rate-green { color: #00c4b4; }
        .sp-tax-note {
          max-width: 640px;
          margin: 20px auto 0;
          text-align: center;
          font-size: 0.85rem;
          color: #6b7280;
          font-style: italic;
          line-height: 1.6;
        }

        /* ─── RULES ──────────────────────────────────────────── */
        .sp-rules {
          max-width: 720px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }
        .sp-rule {
          display: flex;
          align-items: flex-start;
          gap: 14px;
          padding: 20px;
          border-radius: 12px;
        }
        .sp-rule-no { background: #f4f6fb; border: 1px solid #e5e9f2; }
        .sp-rule-yes { background: rgba(0,196,180,0.08); border: 1px solid rgba(0,196,180,0.25); }
        .sp-rule-icon {
          width: 28px; height: 28px;
          border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
          font-size: 0.9rem;
          font-weight: 800;
        }
        .sp-rule-icon-no { background: #e5e9f2; color: #1a1a2e; }
        .sp-rule-icon-yes { background: #00c4b4; color: #ffffff; }
        .sp-rule-text { font-size: 0.9rem; font-weight: 600; color: #1a1a2e; line-height: 1.5; }

        /* ─── FLOW DIAGRAM ───────────────────────────────────── */
        .sp-flow {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0;
          max-width: 820px;
          margin: 0 auto 32px;
          flex-wrap: wrap;
        }
        .sp-flow-node {
          background: rgba(255,255,255,0.07);
          border: 1px solid rgba(255,255,255,0.12);
          border-radius: 14px;
          padding: 22px 24px;
          text-align: center;
          min-width: 160px;
          flex: 1;
          position: relative;
        }
        .sp-flow-node-icon {
          width: 44px; height: 44px;
          background: rgba(0,196,180,0.15);
          border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
          margin: 0 auto 12px;
          color: #00c4b4;
        }
        .sp-flow-node-title {
          font-family: 'Manrope', sans-serif;
          font-size: 0.95rem;
          font-weight: 800;
          color: #ffffff;
          margin-bottom: 4px;
        }
        .sp-flow-node-sub { font-size: 0.78rem; color: rgba(255,255,255,0.45); }
        .sp-flow-arrow {
          color: #00c4b4;
          padding: 0 8px;
          font-size: 1.4rem;
          font-weight: 300;
          flex-shrink: 0;
        }
        .sp-flow-caption {
          max-width: 820px;
          margin: 0 auto;
          text-align: center;
          position: relative;
        }
        .sp-flow-caption p {
          font-size: 1rem;
          color: rgba(255,255,255,0.55);
          line-height: 1.7;
          font-style: italic;
        }

        /* ─── BORROWING CARD ─────────────────────────────────── */
        .sp-borrow {
          max-width: 680px;
          margin: 0 auto;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(0,196,180,0.2);
          border-radius: 18px;
          padding: 40px 36px;
          text-align: center;
          position: relative;
        }
        .sp-borrow-icon {
          width: 56px; height: 56px;
          background: rgba(0,196,180,0.12);
          border-radius: 14px;
          display: flex; align-items: center; justify-content: center;
          margin: 0 auto 20px;
          color: #00c4b4;
        }
        .sp-borrow-title {
          font-family: 'Manrope', sans-serif;
          font-size: 1.5rem;
          font-weight: 800;
          color: #ffffff;
          margin-bottom: 14px;
          line-height: 1.2;
        }
        .sp-borrow-desc {
          font-size: 1rem;
          color: rgba(255,255,255,0.6);
          line-height: 1.75;
        }
        .sp-borrow-desc strong { color: #00c4b4; }

        /* ─── EXAMPLE CARD ───────────────────────────────────── */
        .sp-example {
          max-width: 900px;
          margin: 0 auto;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 8px 48px rgba(0,0,0,0.08);
          border: 1px solid #e5e9f2;
        }
        .sp-example-head {
          background: #0d1b3e;
          padding: 28px 36px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 12px;
        }
        .sp-example-head-title {
          font-family: 'Manrope', sans-serif;
          font-size: 1.1rem;
          font-weight: 700;
          color: #ffffff;
        }
        .sp-example-head-sub {
          font-size: 0.75rem;
          color: rgba(255,255,255,0.45);
          margin-top: 3px;
          letter-spacing: 0.05em;
          text-transform: uppercase;
        }
        .sp-example-badge {
          background: rgba(0,196,180,0.15);
          border: 1px solid rgba(0,196,180,0.3);
          color: #00c4b4;
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          padding: 6px 16px;
          border-radius: 100px;
        }
        .sp-example-start {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          border-bottom: 1px solid #f0f2f8;
        }
        .sp-example-cell {
          padding: 24px 20px;
          border-right: 1px solid #f0f2f8;
          text-align: center;
        }
        .sp-example-cell:last-child { border-right: none; }
        .sp-example-cell-label {
          font-size: 0.64rem;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: #9ca3af;
          margin-bottom: 8px;
        }
        .sp-example-cell-value {
          font-family: 'Manrope', sans-serif;
          font-size: 1.2rem;
          font-weight: 800;
          color: #1a1a2e;
        }
        .sp-example-cell-value.teal { color: #009e90; }
        .sp-example-outcome {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          background: #0d1b3e;
        }
        .sp-example-outcome-cell {
          padding: 30px 24px;
          text-align: center;
          border-right: 1px solid rgba(255,255,255,0.07);
        }
        .sp-example-outcome-cell:last-child { border-right: none; }
        .sp-example-outcome-label {
          font-size: 0.65rem;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.4);
          margin-bottom: 10px;
        }
        .sp-example-outcome-stat {
          font-family: 'Manrope', sans-serif;
          font-size: 1.8rem;
          font-weight: 800;
          color: #00c4b4;
          line-height: 1;
          margin-bottom: 6px;
        }
        .sp-example-outcome-desc {
          font-size: 0.78rem;
          color: rgba(255,255,255,0.4);
        }

        /* ─── KEY DIFFERENCE ─────────────────────────────────── */
        .sp-diff {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          max-width: 820px;
          margin: 0 auto;
        }
        .sp-diff-card {
          border-radius: 18px;
          padding: 40px 32px;
          text-align: center;
        }
        .sp-diff-old { background: #f4f6fb; border: 1px solid #e5e9f2; }
        .sp-diff-new { background: #0d1b3e; border: 1px solid rgba(0,196,180,0.2); }
        .sp-diff-icon {
          width: 52px; height: 52px;
          border-radius: 14px;
          display: flex; align-items: center; justify-content: center;
          margin: 0 auto 20px;
        }
        .sp-diff-icon-grey { background: #e5e9f2; }
        .sp-diff-icon-teal { background: rgba(0,196,180,0.15); }
        .sp-diff-label {
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          margin-bottom: 12px;
        }
        .sp-diff-label-muted { color: #9ca3af; }
        .sp-diff-label-teal { color: #00c4b4; }
        .sp-diff-heading {
          font-family: 'Manrope', sans-serif;
          font-size: 1.5rem;
          font-weight: 800;
          margin-bottom: 12px;
          line-height: 1.2;
        }
        .sp-diff-heading-dark { color: #1a1a2e; }
        .sp-diff-heading-white { color: #ffffff; }
        .sp-diff-desc { font-size: 0.9rem; line-height: 1.7; }
        .sp-diff-desc-muted { color: #6b7280; }
        .sp-diff-desc-light { color: rgba(255,255,255,0.55); }

        /* ─── FIT SECTION ────────────────────────────────────── */
        .sp-fit {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
          max-width: 840px;
          margin: 0 auto;
        }
        .sp-fit-card {
          border-radius: 18px;
          padding: 36px 28px;
        }
        .sp-fit-yes { background: rgba(0,196,180,0.07); border: 1px solid rgba(0,196,180,0.22); }
        .sp-fit-no { background: #f4f6fb; border: 1px solid #e5e9f2; }
        .sp-fit-heading {
          font-family: 'Manrope', sans-serif;
          font-size: 1.1rem;
          font-weight: 800;
          margin-bottom: 20px;
        }
        .sp-fit-heading-green { color: #00c4b4; }
        .sp-fit-heading-red { color: #1a1a2e; }
        .sp-fit-list { list-style: none; display: flex; flex-direction: column; gap: 12px; }
        .sp-fit-item {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          font-size: 0.9rem;
          color: #1a1a2e;
          line-height: 1.5;
        }
        .sp-fit-dot {
          width: 20px; height: 20px;
          border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
          margin-top: 1px;
          font-size: 0.7rem;
          font-weight: 800;
        }
        .sp-fit-dot-yes { background: rgba(0,196,180,0.15); color: #00c4b4; }
        .sp-fit-dot-no { background: #e5e9f2; color: #6b7280; }

        /* ─── BUTTONS ────────────────────────────────────────── */
        .sp-btn {
          display: inline-block;
          font-family: 'Inter', sans-serif;
          font-weight: 700;
          text-decoration: none;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.22s ease;
          border: 2px solid transparent;
          letter-spacing: 0.02em;
          white-space: nowrap;
        }
        .sp-btn-primary {
          padding: 18px 56px;
          font-size: 1.05rem;
          background: #00c4b4;
          color: #0d1b3e;
          border-color: #00c4b4;
        }
        .sp-btn-primary:hover {
          background: #00dccb;
          border-color: #00dccb;
          transform: translateY(-2px);
          box-shadow: 0 10px 28px rgba(0,196,180,0.38);
        }
        .sp-btn-nudge {
          font-size: 0.82rem;
          color: #9ca3af;
          text-align: center;
        }

        /* ─── CTA SECTION ────────────────────────────────────── */
        .sp-cta-card {
          max-width: 820px;
          margin: 0 auto;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(0,196,180,0.2);
          border-radius: 24px;
          padding: 56px 48px;
          position: relative;
        }
        .sp-cta-card-grid {
          display: grid;
          grid-template-columns: 1fr auto;
          gap: 48px;
          align-items: center;
        }
        .sp-cta-card h3 {
          font-family: 'Manrope', sans-serif;
          font-size: clamp(1.4rem, 3vw, 2rem);
          font-weight: 800;
          color: #ffffff;
          margin-bottom: 14px;
          line-height: 1.2;
        }
        .sp-cta-card p {
          font-size: 0.95rem;
          color: rgba(255,255,255,0.55);
          line-height: 1.75;
          margin-bottom: 24px;
        }
        .sp-cta-list {
          list-style: none;
          display: flex;
          flex-direction: column;
          gap: 10px;
          margin-bottom: 32px;
        }
        .sp-cta-list-item {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 0.9rem;
          color: rgba(255,255,255,0.75);
        }
        .sp-cta-list-item svg { color: #00c4b4; flex-shrink: 0; }
        .sp-cta-proof {
          display: flex;
          flex-direction: column;
          gap: 14px;
          min-width: 220px;
          text-align: center;
        }
        .sp-cta-proof-item {
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 12px;
          padding: 16px 20px;
        }
        .sp-cta-proof-stat {
          font-family: 'Manrope', sans-serif;
          font-size: 1.5rem;
          font-weight: 800;
          color: #00c4b4;
          line-height: 1;
          margin-bottom: 4px;
        }
        .sp-cta-proof-label {
          font-size: 0.75rem;
          color: rgba(255,255,255,0.4);
        }

        /* ─── FOOTER CTA ─────────────────────────────────────── */
        .sp-footer {
          background: linear-gradient(135deg, #0a1628 0%, #0d1b3e 55%, #0a1628 100%);
          padding: 104px 24px;
          text-align: center;
          position: relative;
          overflow: hidden;
        }
        .sp-footer::before {
          content: '';
          position: absolute;
          inset: 0;
          background: radial-gradient(ellipse at 50% 110%, rgba(0,196,180,0.12) 0%, transparent 55%);
        }
        .sp-footer-inner { position: relative; max-width: 640px; margin: 0 auto; }
        .sp-footer h2 {
          font-family: 'Manrope', sans-serif;
          font-size: clamp(2rem, 5vw, 3.4rem);
          font-weight: 800;
          color: #ffffff;
          margin-bottom: 18px;
          line-height: 1.12;
        }
        .sp-footer h2 em { font-style: normal; color: #00c4b4; }
        .sp-footer p {
          color: rgba(255,255,255,0.5);
          margin-bottom: 44px;
          font-size: 1.1rem;
          line-height: 1.75;
        }
        .sp-footer-trust {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 24px;
          flex-wrap: wrap;
          margin-top: 24px;
        }
        .sp-footer-trust-item {
          display: flex; align-items: center; gap: 6px;
          font-size: 0.8rem;
          color: rgba(255,255,255,0.35);
        }
        .sp-footer-trust-item svg { color: rgba(0,196,180,0.6); }

        /* ─── DIVIDER ────────────────────────────────────────── */
        .sp-divider {
          border: none;
          height: 1px;
          background: linear-gradient(to right, transparent, #d1d9f0, transparent);
          margin: 0;
        }

        /* ─── LOGO STRIP ─────────────────────────────────────── */
        .sp-logo-strip {
          padding: 22px 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-top: 1px solid #e5e9f2;
          border-bottom: 1px solid #e5e9f2;
          background: #fafbff;
        }
        .sp-brand { display: flex; align-items: center; gap: 14px; }
        .sp-brand-name {
          font-family: 'Manrope', sans-serif;
          font-size: 1.35rem;
          font-weight: 800;
          color: #0d1b3e;
        }

        /* ─── RESPONSIVE ─────────────────────────────────────── */
        @media (max-width: 900px) {
          .sp-cta-card-grid { grid-template-columns: 1fr; }
          .sp-cta-proof { flex-direction: row; justify-content: center; min-width: unset; }
          .sp-example-start { grid-template-columns: repeat(2, 1fr); }
          .sp-example-cell:nth-child(2) { border-right: none; }
          .sp-example-cell:nth-child(1),
          .sp-example-cell:nth-child(2) { border-bottom: 1px solid #f0f2f8; }
        }
        @media (max-width: 768px) {
          .sp-hero { padding: 64px 24px 72px; }
          .sp-gap-grid { grid-template-columns: 1fr; }
          .sp-compare { grid-template-columns: 1fr; }
          .sp-diff { grid-template-columns: 1fr; }
          .sp-fit { grid-template-columns: 1fr; }
          .sp-rules { grid-template-columns: 1fr; }
          .sp-control-grid { grid-template-columns: 1fr 1fr; }
          .sp-flow { flex-direction: column; }
          .sp-flow-arrow { transform: rotate(90deg); }
          .sp-cta-card { padding: 40px 28px; }
          .sp-example-outcome { grid-template-columns: 1fr; }
          .sp-example-outcome-cell { border-right: none; border-bottom: 1px solid rgba(255,255,255,0.07); }
          .sp-example-outcome-cell:last-child { border-bottom: none; }
        }
        @media (max-width: 480px) {
          .sp-control-grid { grid-template-columns: 1fr; }
          .sp-example-start { grid-template-columns: 1fr 1fr; }
          .sp-cta-proof { flex-direction: column; }
          .sp-btn-primary { padding: 17px 36px; font-size: 1rem; }
        }
      `}</style>

      <div className="sp">

        {/* ── HERO ──────────────────────────────────────────── */}
        <section className="sp-hero">
          <div className="sp-eyebrow">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
            SMSF Property Strategy · For Everyday Australians
          </div>
          <h1>
            Using Your Super to Build<br />
            <em>Property Wealth</em>
          </h1>
          <p className="sp-hero-sub">
            How everyday Australians are turning their superannuation into real retirement income —
            through a strategy most people don't even know they have access to.
          </p>
          <p className="sp-hero-pause">
            "This isn't about selling you a property.<br />
            It's about showing you an option you may have never been offered."
          </p>
          <div className="sp-hero-trust">
            <div className="sp-hero-trust-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              General education only
            </div>
            <div className="sp-hero-trust-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              No obligation
            </div>
            <div className="sp-hero-trust-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              Speak with your adviser before acting
            </div>
          </div>
        </section>

        {/* ── DISCLAIMER ──────────────────────────────────── */}
        <div className="sp-disclaimer">
          <p>
            <strong>Important:</strong> The information on this page is general in nature and does not constitute personal financial advice.
            Examples are illustrative only and may not reflect your circumstances.
            Always speak with your accountant or licensed financial adviser before making investment decisions.
          </p>
        </div>

        {/* ── THE RETIREMENT QUESTION ─────────────────────── */}
        <section className="sp-section-dark">
          <div className="sp-big-q">
            <div style={{ display: 'inline-block', background: 'rgba(0,196,180,0.12)', border: '1px solid rgba(0,196,180,0.28)', color: '#00c4b4', fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', padding: '7px 18px', borderRadius: '100px', marginBottom: 32, position: 'relative' }}>
              The Retirement Question
            </div>
            <div className="sp-big-q-text">
              "Will your super actually<br />fund your retirement?"
            </div>
            <p className="sp-big-q-sub">
              Most people assume it will. Almost nobody has actually calculated it.
              Here's what the numbers reveal — and why the answer may surprise you.
            </p>
          </div>
        </section>

        {/* ── THE RETIREMENT GAP ──────────────────────────── */}
        <section className="sp-section">
          <div className="sp-header">
            <div className="sp-badge sp-badge-red">The Retirement Gap</div>
            <h2>A Balance Sounds Big.<br />Until You Turn It Into Income.</h2>
            <p>
              The average Australian retires with somewhere between $300,000 and $400,000 in super.
              A comfortable retirement costs around $70,000 a year. The maths doesn't work.
            </p>
          </div>
          <div className="sp-gap-grid">
            <div className="sp-gap-card sp-gap-card-have">
              <div className="sp-gap-label sp-gap-label-muted">Average super at retirement</div>
              <div className="sp-gap-stat sp-gap-stat-dark">$350,000</div>
              <div className="sp-gap-desc sp-gap-desc-muted">
                Sounds like a lot.<br />At a 4% drawdown rate, it generates<br />
                <strong style={{ color: '#dc2626' }}>≈ $14,000 per year.</strong>
              </div>
            </div>
            <div className="sp-gap-card sp-gap-card-need">
              <div className="sp-gap-label sp-gap-label-light">Comfortable retirement income</div>
              <div className="sp-gap-stat sp-gap-stat-teal">$70,000<span style={{ fontSize: '1.2rem', color: 'rgba(255,255,255,0.4)' }}>/yr</span></div>
              <div className="sp-gap-desc sp-gap-desc-light">
                The ASFA Comfortable Standard for couples.<br />
                <strong style={{ color: 'rgba(255,255,255,0.75)' }}>A $56,000 annual shortfall.</strong>
              </div>
            </div>
          </div>
          <div className="sp-gap-verdict">
            <p>
              "$350,000 doesn't create a $70k income.<br />
              It creates stress — and a retirement you spend managing, not enjoying."
            </p>
          </div>
        </section>

        <hr className="sp-divider" />

        {/* ── YOU DON'T CONTROL YOUR SUPER ────────────────── */}
        <section className="sp-section-dark">
          <div className="sp-header sp-header-dark" style={{ marginBottom: 40 }}>
            <div className="sp-badge sp-badge-teal">What Your Super Actually Is</div>
            <h2>Your Super Is Not Sitting in a Vault.</h2>
            <p>It's invested in markets you don't control — by people you've never met.</p>
          </div>
          <div className="sp-control-grid">
            {[
              { label: 'Australian Shares', sub: 'ASX-listed equities' },
              { label: 'International Markets', sub: 'Global exposure, global risk' },
              { label: 'Managed Funds', sub: 'Pooled, passive, averaged' },
              { label: 'Bonds & Fixed Interest', sub: 'Low-growth "safety" assets' },
            ].map((item, i) => (
              <div className="sp-control-card" key={i}>
                <div className="sp-control-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
                    <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
                  </svg>
                </div>
                <div>
                  <div className="sp-control-label">{item.label}</div>
                  <div className="sp-control-sub">{item.sub}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="sp-control-verdict" style={{ marginTop: 48 }}>
            <div className="sp-control-verdict-text">
              You don't control your super.<br />Someone else does.
            </div>
            <p className="sp-control-verdict-sub">
              Most Australians check their super balance once a year — and hope.
            </p>
          </div>
        </section>

        <hr className="sp-divider" />

        {/* ── SMSF EXPLAINED ──────────────────────────────── */}
        <section className="sp-section">
          <div className="sp-header">
            <div className="sp-badge sp-badge-blue">The Solution</div>
            <h2>Self-Managed Super,<br />Explained Simply.</h2>
            <p>
              A Self-Managed Super Fund is still superannuation — same laws, same retirement age,
              same tax environment. The only difference is who makes the investment decisions.
            </p>
          </div>
          <div className="sp-compare">
            <div className="sp-compare-card sp-compare-old">
              <div className="sp-compare-label sp-compare-label-muted">Traditional Super</div>
              <div className="sp-compare-title sp-compare-title-dark">Fund Manager Chooses</div>
              <div className="sp-compare-desc sp-compare-desc-muted">
                Your money is pooled with thousands of other members. A fund manager decides where
                it goes, how it's weighted, and when it moves.
              </div>
              <span className="sp-compare-note sp-compare-note-grey">You observe. They decide.</span>
            </div>
            <div className="sp-compare-card sp-compare-new">
              <div className="sp-compare-label sp-compare-label-teal">SMSF</div>
              <div className="sp-compare-title sp-compare-title-white">You Choose</div>
              <div className="sp-compare-desc sp-compare-desc-light">
                You become the trustee of your own fund. You select the investments, the strategy,
                and the assets — including property — within ATO guidelines.
              </div>
              <span className="sp-compare-note sp-compare-note-teal">You decide. You benefit.</span>
            </div>
          </div>
        </section>

        {/* ── TAX ADVANTAGE ───────────────────────────────── */}
        <section className="sp-section-alt">
          <div className="sp-header">
            <div className="sp-badge sp-badge-green">The Tax Advantage</div>
            <h2>Super Is Australia's Most Tax-Effective<br />Investment Environment.</h2>
            <p>
              This is why SMSF strategies exist. The tax differential between personal investing
              and super is significant — and in retirement, extraordinary.
            </p>
          </div>
          <div className="sp-tax-table">
            <div className="sp-tax-row sp-tax-row-head">
              <span>Where You Invest</span>
              <span style={{ textAlign: 'right' }}>Tax Rate</span>
            </div>
            <div className="sp-tax-row">
              <div className="sp-tax-cell sp-tax-cell-bg-1">
                <div className="sp-tax-label">Personal Income</div>
                <div className="sp-tax-sub">Standard marginal rate for most Australians</div>
              </div>
              <div className="sp-tax-cell sp-tax-cell-bg-1">
                <span className="sp-tax-rate-red">Up to 47%</span>
              </div>
            </div>
            <div className="sp-tax-row">
              <div className="sp-tax-cell sp-tax-cell-bg-2">
                <div className="sp-tax-label">Super (Accumulation Phase)</div>
                <div className="sp-tax-sub">Same investments, dramatically lower tax</div>
              </div>
              <div className="sp-tax-cell sp-tax-cell-bg-2">
                <span className="sp-tax-rate-amber">15%</span>
              </div>
            </div>
            <div className="sp-tax-row" style={{ borderBottom: 'none' }}>
              <div className="sp-tax-cell sp-tax-cell-bg-3" style={{ borderBottom: 'none' }}>
                <div className="sp-tax-label">Super (Retirement Phase)</div>
                <div className="sp-tax-sub">Investment income — after preservation age</div>
              </div>
              <div className="sp-tax-cell sp-tax-cell-bg-3" style={{ borderBottom: 'none' }}>
                <span className="sp-tax-rate-green">0%</span>
              </div>
            </div>
          </div>
          <p className="sp-tax-note">
            Zero tax on investment income in retirement. This is not a loophole — it's how the system was designed.
          </p>
        </section>

        <hr className="sp-divider" />

        {/* ── PROPERTY INSIDE SUPER ───────────────────────── */}
        <section className="sp-section-dark">
          <div className="sp-header sp-header-dark" style={{ marginBottom: 48 }}>
            <div className="sp-badge sp-badge-teal">The Big Reveal</div>
            <h2 style={{ fontSize: 'clamp(2.2rem, 5vw, 3.6rem)' }}>
              Yes — you <em style={{ fontStyle: 'normal', color: '#00c4b4' }}>can</em> buy property inside your super.
            </h2>
            <p>With an SMSF and a limited recourse borrowing arrangement, your fund can purchase real property as a retirement asset.</p>
          </div>

          {/* Rules */}
          <div style={{ maxWidth: 720, margin: '0 auto 56px' }}>
            <div style={{ textAlign: 'center', marginBottom: 28, fontSize: '0.85rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', position: 'relative' }}>
              The Rules of SMSF Property
            </div>
            <div className="sp-rules">
              <div className="sp-rule sp-rule-no">
                <div className="sp-rule-icon sp-rule-icon-no">✕</div>
                <div className="sp-rule-text">Cannot live in or use the property yourself</div>
              </div>
              <div className="sp-rule sp-rule-no">
                <div className="sp-rule-icon sp-rule-icon-no">✕</div>
                <div className="sp-rule-text">Cannot rent to family members or related parties</div>
              </div>
              <div className="sp-rule sp-rule-no">
                <div className="sp-rule-icon sp-rule-icon-no">✕</div>
                <div className="sp-rule-text">Cannot renovate or improve the property using personal labour</div>
              </div>
              <div className="sp-rule sp-rule-yes">
                <div className="sp-rule-icon sp-rule-icon-yes">✓</div>
                <div className="sp-rule-text">Must be for the sole purpose of retirement benefit</div>
              </div>
            </div>
          </div>

          {/* Flow */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ textAlign: 'center', marginBottom: 28, fontSize: '0.85rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', position: 'relative' }}>
              How the purchase works
            </div>
          </div>
          <div className="sp-flow">
            <div className="sp-flow-node">
              <div className="sp-flow-node-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5v4c0 1.66 4.03 3 9 3s9-1.34 9-3V5"/>
                  <path d="M3 9v4c0 1.66 4.03 3 9 3s9-1.34 9-3V9"/>
                </svg>
              </div>
              <div className="sp-flow-node-title">Your SMSF</div>
              <div className="sp-flow-node-sub">Existing super balance</div>
            </div>
            <div className="sp-flow-arrow">→</div>
            <div className="sp-flow-node">
              <div className="sp-flow-node-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                </svg>
              </div>
              <div className="sp-flow-node-title">Deposit</div>
              <div className="sp-flow-node-sub">Super funds the deposit</div>
            </div>
            <div className="sp-flow-arrow">→</div>
            <div className="sp-flow-node">
              <div className="sp-flow-node-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/>
                </svg>
              </div>
              <div className="sp-flow-node-title">Bank Loan</div>
              <div className="sp-flow-node-sub">LRBA borrowing arrangement</div>
            </div>
            <div className="sp-flow-arrow">→</div>
            <div className="sp-flow-node">
              <div className="sp-flow-node-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
                </svg>
              </div>
              <div className="sp-flow-node-title">Property</div>
              <div className="sp-flow-node-sub">Owned inside the fund</div>
            </div>
          </div>
          <div className="sp-flow-caption" style={{ marginTop: 28 }}>
            <p>Rent income + ongoing contributions service the loan. Time does the rest.</p>
          </div>

          {/* Limited Recourse */}
          <div style={{ marginTop: 56 }}>
            <div className="sp-borrow">
              <div className="sp-borrow-icon">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
              </div>
              <div className="sp-borrow-title">The Safety Feature: Limited Recourse Borrowing</div>
              <p className="sp-borrow-desc">
                If the loan ever defaults, the bank can only claim <strong>the property</strong> —
                not the rest of your super fund. Your other retirement savings are protected.
                This is what makes SMSF borrowing structurally different from personal debt.
              </p>
            </div>
          </div>
        </section>

        {/* ── LOGO STRIP ──────────────────────────────────── */}
        <div className="sp-logo-strip">
          <div className="sp-brand">
            <img src="/6163874b34ebcf3588aeb61e_one-logo.svg" alt="One Property Australia" style={{ height: 44, width: 'auto' }} />
            <div className="sp-brand-name">One Property Australia</div>
          </div>
        </div>
        <hr className="sp-divider" />

        {/* ── REAL EXAMPLE ────────────────────────────────── */}
        <section className="sp-section">
          <div className="sp-header">
            <div className="sp-badge sp-badge-green">Realistic Scenario</div>
            <h2>Time Does the Work.<br />Here's the Proof.</h2>
            <p>
              This couple didn't add extra savings. They didn't change jobs or take on risk they
              couldn't afford. They simply redirected their super — and let compounding do its job.
            </p>
          </div>
          <div className="sp-example">
            <div className="sp-example-head">
              <div>
                <div className="sp-example-head-title">Illustrative Scenario — Couple, Age 38</div>
                <div className="sp-example-head-sub">SMSF Property Purchase · 22-Year Timeline</div>
              </div>
              <div className="sp-example-badge">Illustrative Example</div>
            </div>
            <div className="sp-example-start">
              <div className="sp-example-cell">
                <div className="sp-example-cell-label">Combined Super</div>
                <div className="sp-example-cell-value">$230,000</div>
              </div>
              <div className="sp-example-cell">
                <div className="sp-example-cell-label">Property Purchased</div>
                <div className="sp-example-cell-value">$620,000</div>
              </div>
              <div className="sp-example-cell">
                <div className="sp-example-cell-label">Weekly Rent</div>
                <div className="sp-example-cell-value teal">$550 / wk</div>
              </div>
              <div className="sp-example-cell">
                <div className="sp-example-cell-label">Growth Assumption</div>
                <div className="sp-example-cell-value">5% p.a.</div>
              </div>
            </div>
            <div className="sp-example-outcome">
              <div className="sp-example-outcome-cell">
                <div className="sp-example-outcome-label">At Age 60 · Property Value</div>
                <div className="sp-example-outcome-stat">~$1.6M</div>
                <div className="sp-example-outcome-desc">From $620k at 5% over 22 years</div>
              </div>
              <div className="sp-example-outcome-cell">
                <div className="sp-example-outcome-label">Loan Balance</div>
                <div className="sp-example-outcome-stat">$0</div>
                <div className="sp-example-outcome-desc">Fully paid by rent + contributions</div>
              </div>
              <div className="sp-example-outcome-cell">
                <div className="sp-example-outcome-label">Ongoing Rental Income</div>
                <div className="sp-example-outcome-stat">$750+/wk</div>
                <div className="sp-example-outcome-desc">Tax-free in retirement phase</div>
              </div>
            </div>
          </div>
          <p style={{ textAlign: 'center', fontSize: '0.8rem', color: '#9ca3af', marginTop: 16, fontStyle: 'italic' }}>
            Illustrative only. Not a guarantee of returns. Growth rate and rent are assumptions. Speak with a licensed adviser.
          </p>
        </section>

        {/* ── THE KEY DIFFERENCE ──────────────────────────── */}
        <section className="sp-section-alt">
          <div className="sp-header">
            <div className="sp-badge sp-badge-amber">The Key Difference</div>
            <h2>Traditional Super: You Spend It.<br />SMSF Property: It Pays You.</h2>
            <p>
              This is the shift that changes everything. One path runs out.
              The other generates income for as long as you live.
            </p>
          </div>
          <div className="sp-diff">
            <div className="sp-diff-card sp-diff-old">
              <div className="sp-diff-icon sp-diff-icon-grey">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
                  <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                </svg>
              </div>
              <div className="sp-diff-label sp-diff-label-muted">Traditional Super</div>
              <div className="sp-diff-heading sp-diff-heading-dark">You Spend It.</div>
              <div className="sp-diff-desc sp-diff-desc-muted">
                You draw down the balance. Every withdrawal reduces what's left.
                Eventually — statistically within 15–20 years — it's gone.
                Then you rely on the pension.
              </div>
            </div>
            <div className="sp-diff-card sp-diff-new">
              <div className="sp-diff-icon sp-diff-icon-teal">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#00c4b4" strokeWidth="2">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
                </svg>
              </div>
              <div className="sp-diff-label sp-diff-label-teal">SMSF Property</div>
              <div className="sp-diff-heading sp-diff-heading-white">It Pays You.</div>
              <div className="sp-diff-desc sp-diff-desc-light">
                Instead of withdrawing a shrinking pool of money, you receive rent every week.
                The asset remains. The income continues. The asset can be passed to your estate.
              </div>
            </div>
          </div>
        </section>

        <hr className="sp-divider" />

        {/* ── WHO IT'S FOR ────────────────────────────────── */}
        <section className="sp-section">
          <div className="sp-header">
            <div className="sp-badge sp-badge-amber">Is This Right for You?</div>
            <h2>This Strategy Is Not for Everyone.<br />And That's Important.</h2>
            <p>
              We'd rather you know it's not the right fit than pursue something that doesn't serve you.
              Here's an honest look at who this works for — and who it doesn't.
            </p>
          </div>
          <div className="sp-fit">
            <div className="sp-fit-card sp-fit-yes">
              <div className="sp-fit-heading sp-fit-heading-green">Good Candidate</div>
              <ul className="sp-fit-list">
                {[
                  'Stable, reliable employment income',
                  '$150,000–$300,000+ in super (combined)',
                  '10 or more years until retirement',
                  'Willing to be actively involved in decisions',
                  'Comfortable holding a long-term asset',
                ].map((item, i) => (
                  <li className="sp-fit-item" key={i}>
                    <span className="sp-fit-dot sp-fit-dot-yes">✓</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="sp-fit-card sp-fit-no">
              <div className="sp-fit-heading sp-fit-heading-red">Not Suitable</div>
              <ul className="sp-fit-list">
                {[
                  'Within 5 years of planned retirement',
                  'Irregular or uncertain income',
                  'Super balance under $100,000',
                  'Looking for short-term or liquid returns',
                  'Not willing to take on trustee responsibilities',
                ].map((item, i) => (
                  <li className="sp-fit-item" key={i}>
                    <span className="sp-fit-dot sp-fit-dot-no">✕</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* ── THE REAL RISK ────────────────────────────────── */}
        <section className="sp-section-dark">
          <div className="sp-big-q" style={{ position: 'relative' }}>
            <div style={{ display: 'inline-block', background: 'rgba(220,38,38,0.15)', border: '1px solid rgba(220,38,38,0.3)', color: '#f87171', fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', padding: '7px 18px', borderRadius: '100px', marginBottom: 32, position: 'relative' }}>
              The Real Risk
            </div>
            <div className="sp-big-q-text">
              "The biggest risk...<br />is doing nothing for 20 years."
            </div>
            <p className="sp-big-q-sub">
              The next step isn't to buy a property. The next step is to find out whether
              you even qualify — so you can make an informed decision, not a rushed one.
            </p>
          </div>
        </section>

        {/* ── CTA ─────────────────────────────────────────── */}
        <section className="sp-section-dark" style={{ paddingTop: 0 }}>
          <div className="sp-cta-card">
            <div className="sp-cta-card-grid">
              <div>
                <h3>Request Your Free<br />Suitability Assessment</h3>
                <p>
                  If you'd like us to assess whether purchasing a property inside your super
                  is possible for your situation, reach out below. No cost. No obligation.
                  Just clarity on where you stand.
                </p>
                <ul className="sp-cta-list">
                  {[
                    'Your borrowing capacity inside super',
                    'An estimated retirement outcome based on your balance',
                    'Whether this strategy is suited to your situation',
                    'A clear next step — whatever that looks like for you',
                  ].map((item, i) => (
                    <li className="sp-cta-list-item" key={i}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                      {item}
                    </li>
                  ))}
                </ul>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 10 }}>
                  <a href="mailto:info@onepropertyaustralia.com.au?subject=SMSF Suitability Assessment Request" className="sp-btn sp-btn-primary">
                    Request My Free Assessment
                  </a>
                  <span className="sp-btn-nudge" style={{ color: 'rgba(255,255,255,0.35)' }}>No cost · No obligation · We'll respond within 1 business day</span>
                </div>
              </div>
              <div className="sp-cta-proof">
                <div className="sp-cta-proof-item">
                  <div className="sp-cta-proof-stat">$150K+</div>
                  <div className="sp-cta-proof-label">Minimum super to consider this strategy</div>
                </div>
                <div className="sp-cta-proof-item">
                  <div className="sp-cta-proof-stat">0%</div>
                  <div className="sp-cta-proof-label">Tax on investment income in retirement phase</div>
                </div>
                <div className="sp-cta-proof-item">
                  <div className="sp-cta-proof-stat">15%</div>
                  <div className="sp-cta-proof-label">Tax rate inside super vs up to 47% personally</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── FOOTER CLOSER ───────────────────────────────── */}
        <section className="sp-footer" id="contact">
          <div className="sp-footer-inner">
            <h2>
              Your retirement will happen<br /><em>either way.</em>
            </h2>
            <p>
              The question is whether you design it — or hope for it.
              A free assessment costs you nothing. Doing nothing for another decade costs you everything.
            </p>
            <a href="mailto:info@onepropertyaustralia.com.au?subject=SMSF Suitability Assessment Request" className="sp-btn sp-btn-primary">
              Book My Free Assessment
            </a>
            <div className="sp-footer-trust">
              <div className="sp-footer-trust-item">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                No obligation
              </div>
              <div className="sp-footer-trust-item">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                Respond within 1 business day
              </div>
              <div className="sp-footer-trust-item">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                General education · Not financial advice
              </div>
            </div>
          </div>
        </section>

      </div>
    </>
  );
};

export default Superannuation;
